import { IPropWithClassName } from '@/types'
import clsx from 'clsx'
import { ReactNode, useEffect, useRef } from 'react'
import { IntersectionOptions, useInView } from 'react-intersection-observer'

interface WrapperWithInViewProps extends IPropWithClassName {
	children: (_inView: boolean, _endInView?: boolean) => ReactNode
	inViewOptions?: IntersectionOptions
	/**
	 * @description if true, inView will be false only when scroll to bottom, not up
	 * @default true
	 */
	isWhenScrollBottomOnly?: boolean
	/**
	 * @description called whenever the resolved inView value changes, so a parent
	 * can react to the same trigger the children already receive
	 */
	onInViewChange?: (_inView: boolean) => void
}

export const WrapperWithInView = ({
	children,
	inViewOptions,
	className,
	isWhenScrollBottomOnly = true,
	onInViewChange,
}: WrapperWithInViewProps) => {
	const { ref, inView, entry } = useInView(inViewOptions)
	let _inView = inView

	if (isWhenScrollBottomOnly && entry?.boundingClientRect && !inView) {
		_inView = entry.boundingClientRect.y > 0
	}

	// hold the callback in a ref so callers passing an inline function do not
	// re-fire the effect on every render
	const refOnInViewChange = useRef(onInViewChange)
	useEffect(() => {
		refOnInViewChange.current = onInViewChange
	})

	useEffect(() => {
		refOnInViewChange.current?.(_inView)
	}, [_inView])

	return (
		<>
			<div
				className={clsx('WrapperWithInView-trigger', className)}
				ref={ref}
			/>
			{children(_inView)}
		</>
	)
}
